import Mention from "@tiptap/extension-mention";
import type { Node as PMNode } from "@tiptap/pm/model";
import { mergeAttributes, ReactNodeViewRenderer } from "@tiptap/react";
import { createElement } from "react";
import { coveredAtomSelectionPlugin } from "./plugins/covered-atom-selection";
import { MentionReferenceNodeView } from "./views/mention-reference-node-view";

export function renderMentionPlaintext(attrs: Record<string, unknown>): string {
  const label =
    typeof attrs.label === "string" && attrs.label.trim().length > 0
      ? attrs.label
      : typeof attrs.id === "string" && attrs.id.trim().length > 0
        ? attrs.id
        : "mention";
  const normalized = label.trim().replace(/^@+/, "");
  const escaped = normalized.replaceAll("[", "\\[").replaceAll("]", "\\]");
  return `[${escaped}]`;
}

/**
 * Mention extension with explicit type metadata.
 *
 * Stores `mentionType` on each mention node and serializes it to
 * `data-mention-type` so references can be persisted reliably.
 */
export const MentionNode = Mention.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      mentionType: {
        default: null,
        parseHTML: (element) => element.getAttribute("data-mention-type"),
        renderHTML: (attributes) => {
          const mentionType = attributes.mentionType;
          if (typeof mentionType !== "string" || mentionType.length === 0) {
            return {};
          }
          return {
            "data-mention-type": mentionType,
          };
        },
      },
      // The optional version facet of the reference (`ver/12`, `diff/11-12`), spelled exactly
      // as `serializeMentionVersionRef` writes it. `null` — the common case — means the
      // mention points at the element as it is now.
      versionRef: {
        default: null,
        parseHTML: (element) => element.getAttribute("data-mention-version-ref"),
        renderHTML: (attributes) => {
          const versionRef = attributes.versionRef;
          if (typeof versionRef !== "string" || versionRef.length === 0) {
            return {};
          }
          return {
            "data-mention-version-ref": versionRef,
          };
        },
      },
    };
  },

  renderHTML({ node, HTMLAttributes }) {
    const id = typeof node.attrs.id === "string" ? node.attrs.id : null;
    const mentionType =
      typeof node.attrs.mentionType === "string" && node.attrs.mentionType.length > 0
        ? node.attrs.mentionType
        : null;
    const label =
      typeof node.attrs.label === "string" && node.attrs.label.length > 0 ? node.attrs.label : id;
    const serializedLabel = label?.startsWith("@") ? label : `@${label ?? ""}`;

    return [
      "span",
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
        class: "mention reference-chip",
        "data-type": "mention",
        "data-id": id ?? undefined,
        "data-mention-id": id ?? undefined,
        "data-mention-type": mentionType ?? undefined,
      }),
      serializedLabel,
    ];
  },

  renderText({ node }) {
    return renderMentionPlaintext(node.attrs);
  },

  // Tiptap applies every extension's `extendNodeSchema` to every node type, so an
  // unguarded `leafText` becomes the schema-wide leaf renderer and the last extension
  // registered wins. Without this guard a `hardBreak` reports `[mention]` as its text
  // content, corrupting `textContent`/`textBetween` for spellcheck, diff refinement,
  // and paste.
  extendNodeSchema(extension) {
    if (extension.name !== "mention") return {};
    return {
      leafText: (node: PMNode) => renderMentionPlaintext(node.attrs),
    };
  },

  addNodeView() {
    return ReactNodeViewRenderer((props) =>
      createElement(MentionReferenceNodeView, {
        ...props,
      })
    );
  },

  addProseMirrorPlugins() {
    return [...(this.parent?.() ?? []), coveredAtomSelectionPlugin(this.name)];
  },
});

type MentionSuggestionConfig = NonNullable<
  Parameters<typeof MentionNode.configure>[0]
>["suggestion"];

/**
 * Single source of the mention extension's configuration: the legacy `mention` class every
 * surface expects, plus the optional suggestion plugin. Read-only surfaces (diff/version
 * rendering) omit the suggestion entirely rather than passing an empty one.
 */
export function configureMentionNode(suggestion?: MentionSuggestionConfig | null) {
  return MentionNode.configure({
    HTMLAttributes: { class: "mention" },
    ...(suggestion ? { suggestion } : {}),
  });
}
